import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Crown, Edit, Plus, Trash2, Loader2 } from "lucide-react";
import { EmployeeStoreAccess } from "@/components/shared/EmployeeStoreAccess";
import { getDealerCoOwners, createDealerCoOwner, updateDealerCoOwner, deleteDealerCoOwner } from "@/lib/api";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", password: "", allowed_location_ids: null };

export const CoOwnersSection = ({ dealerId, stores = [] }) => {
  const [owners, setOwners] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => getDealerCoOwners(dealerId).then(setOwners).catch(() => setOwners([])), [dealerId]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.email || (!form.id && !form.password)) return toast.error("Email and password are required");
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (form.id) await updateDealerCoOwner(dealerId, form.id, payload); else await createDealerCoOwner(dealerId, payload);
      toast.success(form.id ? "Owner updated" : "Owner added");
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save owner"); }
    setSaving(false);
  };
  const remove = async (o) => {
    if (!window.confirm(`Remove owner login ${o.email}?`)) return;
    try { await deleteDealerCoOwner(dealerId, o.id); toast.success("Owner removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to remove owner"); }
  };

  return (
    <Card className="border-teal/30" data-testid="co-owners-section">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium text-navy flex items-center gap-2"><Crown className="w-4 h-4 text-teal" /> Additional Owners (Partners)</CardTitle>
        <Button size="sm" className="bg-teal hover:bg-teal/90" onClick={() => setForm(EMPTY)} data-testid="add-co-owner-btn"><Plus className="w-4 h-4 mr-1" /> Add Owner</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-slate-500">Partners get full owner access (all tabs, manage employees) for the stores you choose, and receive the dealer emails for those stores.</p>
        {owners.length === 0 && <p className="text-sm text-slate-400">No additional owners</p>}
        {owners.map((o) => (
          <div key={o.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`co-owner-${o.id}`}>
            <div>
              <p className="text-sm font-medium text-navy">{o.first_name} {o.last_name}</p>
              <p className="text-xs text-slate-500">{o.email}{o.phone ? ` | ${o.phone}` : ""} | {Array.isArray(o.allowed_location_ids) ? `${o.allowed_location_ids.length} store(s)` : "All stores"}</p>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setForm({ ...o, password: "", allowed_location_ids: o.allowed_location_ids ?? null })} data-testid={`edit-co-owner-${o.id}`}><Edit className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(o)} data-testid={`delete-co-owner-${o.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </CardContent>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? "Edit" : "Add"} Owner</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First Name</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="co-owner-first-name" /></div>
                <div><Label>Last Name</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="co-owner-last-name" /></div>
              </div>
              <div><Label>Login Email *</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="co-owner-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="co-owner-phone" /></div>
              <div><Label>{form.id ? "New Password (optional)" : "Password *"}</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="co-owner-password" /></div>
              <EmployeeStoreAccess stores={stores} value={form.allowed_location_ids} onChange={(v) => set("allowed_location_ids", v)} />
              <Button onClick={save} disabled={saving} className="w-full bg-teal hover:bg-teal/90" data-testid="save-co-owner-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Owner"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
